import { Component, Input, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { IEditDrawerComponent } from 'src/app/lego-utils';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { defer, EMPTY, finalize, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Task, TaskCode, TaskStatus } from 'src/app/models';
import { IOqcValidationResultContract } from 'src/app/models/domain/oqc-validation-result';
import { ITaskForm } from 'src/app/models/domain/task-form.model';
import {
  EhdValidationService,
  isEhdValidationError,
  isEhdValidationFailedError,
} from 'src/app/services/ehd-validation.service';
import { FeatureFlagService } from 'src/app/services/feature-flag.service';
import { BOMHeaderService, DesignService, TaskService } from 'src/app/state';
import { DevelopmentNoveltyFormService } from '../development-novelty-form.service';

@Component({
  selector: 'em-development-novelty-table-edit',
  styleUrls: ['./development-novelty-table-edit.drawer.less'],
  templateUrl: './development-novelty-table-edit.drawer.html',
  standalone: false,
})
export class DevelopmentNoveltyTableEditDrawer implements IEditDrawerComponent<Task>, OnDestroy {
  public submitError?: string;
  public ehdError = false;
  public ehdErrors: Array<{ rowNumber: number | null; severity: string; text: string }> = [];
  public formGroup?: FormGroup<ITaskForm>;
  public isC47 = false;
  public isLoading = false;

  private readonly subscriptions = new Subscription();

  private readonly ehdValidationEnabled$ =
    this.featureFlagService.isFeatureEnabled('ehd_validation_enabled');

  public constructor(
    private readonly drawerRef: NzDrawerRef<boolean>,
    private readonly taskService: TaskService,
    private readonly taskFormService: DevelopmentNoveltyFormService,
    private readonly notification: NzNotificationService,
    private readonly designService: DesignService,
    private readonly bomHeaderService: BOMHeaderService,
    private readonly ehdValidation: EhdValidationService,
    private readonly featureFlagService: FeatureFlagService
  ) {}

  @Input() public set editValue(task: Task) {
    this.drawerRef.nzTitle = `Edit ${task.explanation}`;
    this.isC47 = task.process === (TaskCode.C47 as string);
    this.formGroup = this.taskFormService.createFormGroup(task);
  }

  public submitForm(formGroup: FormGroup<ITaskForm> | undefined): void {
    if (!formGroup) {
      throw new Error('Trying to save but there was no data');
    }

    // Reset UI state
    this.submitError = undefined;
    this.ehdError = false;
    this.ehdErrors = [];

    const entity = this.taskFormService.adaptToDomain(formGroup);
    const { id, isLockBom } = formGroup.getRawValue();
    const designId = this.designService.getDesignId();

    let finalSubmit$: Observable<void>;

    // The spinner tracked only this call — NgEntityServiceLoader was dispatched around
    // PATCH for the task store, so EHD validation and the BOM-lock chain never spun it.
    // defer, so the flag is set when the update is actually subscribed rather than when
    // the chain is assembled: on the C47 path it runs after EHD validation.
    const update$ = defer(() => {
      this.isLoading = true;

      return this.taskService.update(id, { ...entity, status: TaskStatus.Completed }).pipe(
        map(() => undefined),
        finalize(() => {
          this.isLoading = false;
        })
      );
    });

    // Only run EHD validation when required
    if (this.isC47 && designId) {
      finalSubmit$ = this.ehdValidationEnabled$.pipe(
        switchMap((enabled) => {
          if (!enabled) {
            return update$;
          }

          return this.ensureEhdCompliance(designId).pipe(
            catchError((err) => {
              if (isEhdValidationError(err)) {
                this.ehdError = true;

                this.ehdErrors = err.errors.map((e) => ({
                  severity: e.message.split(':')[0].trim(),
                  text: e.message.split(':').slice(1).join(':').trim(),
                  rowNumber: e.row,
                }));

                this.submitError = undefined;

                return EMPTY;
              }

              if (isEhdValidationFailedError(err)) {
                this.submitError = err.message;
                this.ehdError = false;

                return EMPTY;
              }

              this.submitError =
                err instanceof Error ? err.message : 'Submit failed with an unknown error';

              this.ehdError = false;

              return EMPTY;
            }),
            switchMap(() => update$)
          );
        })
      );
    } else {
      finalSubmit$ = update$;
    }

    // Optional lock BOM chain
    if (isLockBom && designId) {
      finalSubmit$ = finalSubmit$.pipe(
        switchMap(() => this.designService.set40C(designId)),
        switchMap(() => this.bomHeaderService.lockBOM(designId))
      );
    }

    this.subscriptions.add(
      finalSubmit$.subscribe({
        next: () => {
          this.drawerRef.close(true);
          this.notification.success('Milestone updated', 'The milestone was successfully updated');
        },
        error: (error: unknown) => {
          this.submitError =
            error instanceof Error ? error.message : 'Submit failed with an unknown error';
        },
      })
    );
  }

  private ensureEhdCompliance(designId: string): Observable<void> {
    return this.ehdValidation.validateEhd(designId).pipe(
      switchMap((result: IOqcValidationResultContract) => {
        if (result.validationFailed) {
          return throwError(() => ({
            type: 'ehdValidationFailed',
            message: result.failureReason || 'EHD validation failed',
          }));
        }

        if (result.errors.length > 0) {
          return throwError(() => ({ type: 'ehdErrors', errors: result.errors }));
        }

        return of(undefined);
      })
    );
  }

  public trackByEhdError(
    index: number,
    item: { severity: string; text: string; rowNumber: number | null }
  ): number {
    return item.rowNumber ?? index;
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
